import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  useTheme,
  FormHelperText,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { Add } from "@mui/icons-material";
import { createFinancialTransaction } from "../../api/financialTransactions/createfinancialTransactions";
import Swal from "sweetalert2";
import { DocumentType, PaymentType } from "../../components/enums/enums";

const AddFinancialTransactionModal = ({
  open,
  onClose,
  taxpayerList,
  initialTaxpayerId,
  setSearchForTransaction,
}) => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    date: moment(),
    documentType: DocumentType.TAHAKKUK,
    description: "",
    expense: 0,
    collection: 0,
    accrual: 0,
    paymentType: PaymentType.NAKIT,
    taxpayerId: initialTaxpayerId || null,
  });

  // Set taxpayerId when modal opens with initialTaxpayerId
  useEffect(() => {
    if (initialTaxpayerId) {
      setFormData((prev) => ({ ...prev, taxpayerId: initialTaxpayerId }));
    }
  }, [initialTaxpayerId, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // İşlem türü değiştiğinde ilgili alanları sıfırla
    if (name === "documentType") {
      const updatedData = { ...formData, [name]: value };

      // TAHSİLAT seçilirse, expense ve accrual alanlarını sıfırla
      if (value === DocumentType.TAHSILAT) {
        updatedData.expense = 0;
        updatedData.accrual = 0;
      }
      // TEDİYE seçilirse, collection alanını sıfırla
      else if (value === DocumentType.TEDIYE) {
        updatedData.collection = 0;
      }

      setFormData(updatedData);
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      date: newDate,
    });
  };

  // Form doğrulama durum değişkenleri
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Form doğrulaması için fonksiyon
  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) newErrors.date = "Tarih alanı zorunludur";
    if (!formData.taxpayerId)
      newErrors.taxpayerId = "Mükellef seçimi zorunludur";
    if (!formData.documentType)
      newErrors.documentType = "İşlem Türü seçimi zorunludur";
    if (!formData.description)
      newErrors.description = "Açıklama alanı zorunludur";

    // Borç girme zorunluluğu kaldırıldı

    // Ödeme tipi doğrulaması - sadece gerektiğinde
    if (
      (formData.documentType === DocumentType.TAHSILAT ||
        formData.documentType === DocumentType.TEDIYE) &&
      !formData.paymentType
    ) {
      newErrors.paymentType = "Ödeme tipi seçimi zorunludur";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;

    // İşlem türüne göre giriş kısıtlaması
    if (
      (formData.documentType === DocumentType.TAHSILAT &&
        (name === "expense" || name === "accrual")) ||
      (formData.documentType === DocumentType.TEDIYE && name === "collection")
    ) {
      return; // Giriş engellendi
    }

    // Negatif sayı kontrolü
    if (value.startsWith("-")) return;

    // Başındaki gereksiz sıfırları temizle (ondalıklı değilse)
    let cleanedValue = value;
    if (/^0[0-9]+$/.test(value)) {
      cleanedValue = value.replace(/^0+/, "");
    }

    const numValue = parseFloat(cleanedValue) || 0;
    console.log(numValue);

    setFormData({
      ...formData,
      [name]: numValue,
    });
  };

  const handleTaxpayerChange = (event, newValue) => {
    setFormData({
      ...formData,
      taxpayerId: newValue ? newValue.id : null,
    });
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    // Form doğrulaması
    if (!validateForm()) {
      // 3 saniye sonra hata mesajlarını kaldır
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
      return;
    }

    // Format the data for the API request
    const requestData = {
      ...formData,
      date: formData.date.format(),
      paymentType:
        formData.documentType === "TAHSILAT" ||
        formData.documentType === "TEDIYE"
          ? formData.paymentType
          : undefined, // Format the moment object to ISO string
    };

    await createFinancialTransaction(requestData)
      .then((res) => {
        setSearchForTransaction(true);
        Swal.fire({
          text: "Başarıyla Eklendi.",
          icon: "success",
          confirmButtonText: "Tamam",
        });

        onClose();
      })
      .catch((err) => {
        Swal.fire({
          text: "İşlem eklenirken hata oluştu.",
          icon: "error",
          confirmButtonText: "Tamam",
        });
      });
  };

  // Find the current taxpayer object based on taxpayerId
  const currentTaxpayer =
    taxpayerList?.find(
      (taxpayer) => taxpayer.id === (initialTaxpayerId || formData.taxpayerId)
    ) || null;

  const inputStyle = {
    marginBottom: "12px",
    "& .MuiInputBase-root": {
      height: "42px",
    },
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 12px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
  };

  const numberInputStyle = {
    ...inputStyle,
    width: "31%",
  };

  // Ödeme tipi alanı gösterme kontrolü
  const showPaymentTypeField =
    formData.documentType === DocumentType.TAHSILAT ||
    formData.documentType === DocumentType.TEDIYE;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: "white",
          fontWeight: 500,
          padding: "6px 24px",
        }}
      >
        İşlem Ekle
      </DialogTitle>
      <DialogContent sx={{ padding: "20px", mt: "12px" }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box sx={{ height: "auto", paddingTop: "5px" }}>
              <DatePicker
                label="Tarih"
                value={formData.date}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: true,
                    error: submitted && !!errors.date,
                    helperText: submitted && errors.date,
                    sx: {
                      marginBottom: "12px",
                      "& .MuiInputBase-root": {
                        minHeight: "36px",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        lineHeight: "2.1",
                      },
                    },
                  },
                }}
              />
            </Box>

            <Autocomplete
              options={taxpayerList || []}
              getOptionLabel={(option) => {
                return option.title || "";
              }}
              value={currentTaxpayer}
              disabled={!!initialTaxpayerId}
              onChange={handleTaxpayerChange}
              noOptionsText="Mükellef bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mükellef"
                  fullWidth
                  required
                  size="small"
                  error={submitted && !!errors.taxpayerId}
                  helperText={submitted && errors.taxpayerId}
                  sx={inputStyle}
                />
              )}
            />

            <FormControl
              fullWidth
              size="small"
              sx={inputStyle}
              required
              error={submitted && !!errors.documentType}
            >
              <InputLabel>İşlem Türü</InputLabel>
              <Select
                name="documentType"
                value={formData.documentType}
                onChange={handleChange}
                label="İşlem Türü"
              >
                {Object.keys(DocumentType).map((type) => (
                  <MenuItem key={type} value={DocumentType[type]}>
                    {DocumentType[type]}
                  </MenuItem>
                ))}
              </Select>
              {submitted && errors.documentType && (
                <FormHelperText>{errors.documentType}</FormHelperText>
              )}
            </FormControl>

            <TextField
              name="description"
              label="Açıklama"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              required
              size="small"
              error={submitted && !!errors.description}
              helperText={submitted && errors.description}
              sx={inputStyle}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <TextField
                name="expense"
                label="Harcama"
                type="number"
                value={formData.expense}
                onChange={handleNumberChange}
                size="small"
                disabled={formData.documentType === DocumentType.TAHSILAT}
                sx={numberInputStyle}
              />

              <TextField
                name="collection"
                label="Alınan"
                type="number"
                value={formData.collection}
                onChange={handleNumberChange}
                size="small"
                disabled={formData.documentType !== DocumentType.TAHSILAT}
                sx={numberInputStyle}
              />

              <TextField
                name="accrual"
                label="Borç"
                type="number"
                value={formData.accrual}
                onChange={handleNumberChange}
                size="small"
                disabled={formData.documentType === DocumentType.TAHSILAT}
                sx={numberInputStyle}
              />
            </Box>

            {showPaymentTypeField && (
              <FormControl
                fullWidth
                size="small"
                sx={inputStyle}
                required
                error={submitted && !!errors.paymentType}
              >
                <InputLabel>Ödeme Tipi</InputLabel>
                <Select
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  label="Ödeme Tipi"
                >
                  {Object.keys(PaymentType).map((type) => (
                    <MenuItem key={type} value={PaymentType[type]}>
                      {PaymentType[type]}
                    </MenuItem>
                  ))}
                </Select>
                {submitted && errors.paymentType && (
                  <FormHelperText>{errors.paymentType}</FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "flex-end", gap: "12px" }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            minWidth: "100px",
          }}
        >
          Kapat
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          startIcon={<Add />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            minWidth: "100px",
          }}
        >
          Ekle
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFinancialTransactionModal;
